import { getAccessToken } from '@/lib/api';
import type { ChatMessage } from '@/types/api';
import { io, type Socket } from 'socket.io-client';

const WS_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

/** Eventos que el servidor envia al cliente. */
export interface ServerEvents {
  'connection:ready': (payload: {
    userId: string;
    channels: string[];
    onlineUsers: string[];
  }) => void;
  'connection:error': (payload: { message: string }) => void;
  'message:new': (message: ChatMessage) => void;
  'message:deleted': (payload: { messageId: string; channelId: string }) => void;
  'typing:start': (payload: { channelId: string; userId: string; fullName: string }) => void;
  'typing:stop': (payload: { channelId: string; userId: string }) => void;
  'presence:online': (payload: { userId: string }) => void;
  'presence:offline': (payload: { userId: string }) => void;
  'session:revoked': (payload: { reason: string; message: string }) => void;
  error: (payload: { message: string; statusCode?: number }) => void;
}

/** Eventos que el cliente envia al servidor. */
export interface ClientEvents {
  'message:send': (
    payload: { channelId: string; body: string; replyToId?: string },
    ack?: (response: { ok: true; messageId: string }) => void,
  ) => void;
  'message:delete': (payload: { messageId: string }, ack?: (r: { ok: true }) => void) => void;
  'typing:start': (payload: { channelId: string }) => void;
  'typing:stop': (payload: { channelId: string }) => void;
  'channel:read': (payload: { channelId: string }, ack?: (r: { ok: true }) => void) => void;
  'channel:subscribe': (payload: { channelId: string }, ack?: (r: { ok: true }) => void) => void;
}

export type ChatSocket = Socket<ServerEvents, ClientEvents>;

let socket: ChatSocket | null = null;

/**
 * Conexion unica compartida por toda la app. El token viaja en el handshake y
 * se vuelve a leer en cada reconexion, de modo que si el access token se
 * renovo mientras el socket estaba caido, la reconexion usa el nuevo.
 */
export function connectSocket(): ChatSocket {
  if (socket?.connected) {
    return socket;
  }

  socket ??= io(`${WS_URL}/chat`, {
    autoConnect: false,
    withCredentials: true,
    transports: ['websocket'],
    auth: (cb) => cb({ token: getAccessToken() }),
  });

  if (!socket.connected) {
    socket.connect();
  }
  return socket;
}

export function getSocket(): ChatSocket | null {
  return socket;
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
}
